import { currentUserId, currentUserQuery, suiteRoot } from "./auth";

export type FxCompany = { id: number; legal_name?: string; name?: string };
export type FxClientHit = { id: number; name: string; email?: string | null; siret?: string | null; city?: string | null };
export type FxClient = {
  id: number;
  company_id: number;
  name: string;
  email: string | null;
  phone: string | null;
  address_line: string | null;
  postal_code: string | null;
  city: string | null;
  siret: string | null;
  contacts?: Array<{ first_name?: string | null; last_name?: string | null; email?: string | null; phone?: string | null }>;
};

async function fx<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  const userId = currentUserId();
  if (userId) headers.set("X-User-Id", userId);
  if (init?.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  const res = await fetch(`${suiteRoot()}/facturx/api/${path}`, {
    credentials: "include",
    cache: "no-store",
    ...init,
    headers,
  });
  const data = (await res.json().catch(() => ({}))) as { ok?: boolean; message?: string };
  if (!res.ok || data.ok === false) {
    throw new Error(data.message || "Factures ne répond pas.");
  }
  return data as T;
}

export function loadCompanies(): Promise<{ companies: FxCompany[] }> {
  return fx("companies.php");
}

export function searchClients(companyId: number, q: string): Promise<{ clients: FxClientHit[] }> {
  const query = new URLSearchParams({ company_id: String(companyId), q });
  return fx(`clients.php?${query}`);
}

export function readClient(id: number): Promise<{ client: FxClient }> {
  return fx(`clients.php?id=${id}`);
}

export function lookupSiret(q: string): Promise<{ match: Record<string, string | null> }> {
  return fx(`siret-lookup.php?q=${encodeURIComponent(q)}`);
}

export type FxDoc = {
  id: number;
  doc_type: string;
  status: string;
  number: string | null;
  issue_date: string | null;
  total_ttc_cents: number | string | null;
  paid_cents?: number | string | null;
  remaining_cents?: number | string | null;
};

export function listDocuments(companyId: number, clientId: number): Promise<{ documents: FxDoc[] }> {
  const query = new URLSearchParams({ company_id: String(companyId), client_id: String(clientId) });
  return fx(`documents.php?${query}`);
}

export type FxOpenDoc = FxDoc & { client_id?: number; signed_at?: string | null; converted_invoice_id?: number | null; refused_at?: string | null; credit_status?: string | null };

export function listCompanyDocuments(companyId: number, docType: "quote" | "invoice"): Promise<FxOpenDoc[]> {
  const query = new URLSearchParams({ company_id: String(companyId), doc_type: docType, status: "validated" });
  return fx<{ documents?: FxOpenDoc[] }>(`documents.php?${query}`).then((data) => data.documents || []);
}

export type FxSignedQuote = { id: number; number: string; signer: string };

export async function listSignedQuotes(companyId: number, clientId: number): Promise<FxSignedQuote[]> {
  const query = new URLSearchParams({
    company_id: String(companyId),
    client_id: String(clientId),
    doc_type: "quote",
  });
  const data = await fx<{ documents?: Array<FxDoc & { signed_at?: string | null; signer_name?: string | null }> }>(`documents.php?${query}`);
  return (data.documents || []).flatMap((row) => {
    if (row.doc_type !== "quote" || !row.signed_at) return [];
    return [{ id: row.id, number: row.number || `Devis ${row.id}`, signer: row.signer_name || "" }];
  });
}

export function quotePdfUrl(id: number): string {
  const extra = currentUserQuery().replace(/^\?/, "");
  return `${suiteRoot()}/facturx/api/document-pdf.php?id=${id}&inline=1${extra ? `&${extra}` : ""}`;
}

export function createClient(body: Record<string, unknown>): Promise<{ client: FxClient }> {
  return fx("clients.php", { method: "POST", body: JSON.stringify(body) });
}

export type FxProduct = {
  id: number;
  label: string;
  unit_price_ht_cents: number;
  default_qty: number;
  tax_rate_id: number | null;
  product_code: string | null;
};

export function listProducts(companyId: number): Promise<{ products: FxProduct[] }> {
  return fx(`products.php?company_id=${companyId}`);
}

export function loadDefaultTax(companyId: number): Promise<{ default_tax_rate_id: number | null }> {
  return fx(`tax-rates.php?company_id=${companyId}`);
}

export function createDraft(body: {
  company_id: number;
  client_id: number;
  doc_type: "quote" | "invoice";
  lines: Array<{
    product_id: number | null;
    label: string;
    quantity: number;
    unit_price_ht_cents: number;
    tax_rate_id: number | null;
  }>;
}): Promise<{ document: FxDoc }> {
  return fx("documents.php", { method: "POST", body: JSON.stringify(body) });
}
